import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { LanguageDropDownService } from '@navigation';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormHelper } from '@forms';
import { AuthService } from '@auth';
import { ThemeService } from '@themes';
import { Config } from '@config';
import { LanguageService, __i18n } from '@localisation';
import { AuthResultCode } from 'modules/auth-module/auth.interface';

@Component({
    selector: 'login-screen',
    templateUrl: './login.template.html',
    standalone: false
})
export class LoginScreen implements OnDestroy{
    constructor(
        public language: LanguageDropDownService,
        private formBuilder: FormBuilder,
        public authService: AuthService,
        private formHelper: FormHelper,
        private themeService: ThemeService,
        private config: Config,
        private router: Router,
        private languageService: LanguageService
    ) {
        this.initFormBuilder()

        this.showSignup = themeService.currentThemeConfig ? themeService.currentThemeConfig.showSignUp : false;

        this.subscriptions.push(this.themeService.data.subscribe((data: any) => {
            if (data) {
                this.showSignup = data.showSignUp;
                this.disableEmail = data.disableLoginEmail;
                this.disablePassword = data.disableLoginPassword;
                if (this.config.serverConfig.EXT_AUTH_TYPE && data.showExternalLogin) {
                   this.initExtAuthType(data);
                }
                if (data.externalLoginDomains){
                    this.externalLoginDomains = data.externalLoginDomains;
                }
            }
        }));
    }

    private initFormBuilder() {
        this.loginForm = this.formBuilder.group({
            UserName: ['', Validators.required],
            Password: ['', Validators.required],
            RememberMe: ['']
        });
    }

    private initExtAuthType(data) {
        this.showInternalLogin = typeof data.showInternalLogin === 'boolean' ? data.showInternalLogin : true;
        this.showExternalLogin = true;
        this.externalAuthenticationName = data.externalAuthenticationName;
        this.externalLoginText = !this.showInternalLogin 
            ? __i18n('Log in', 'landingpage.button.signin')
            : this.getExtLoginButtonCaption(this.externalAuthenticationName
                || this.supportedLoginNames[this.config.serverConfig.EXT_AUTH_TYPE]
                || this.config.serverConfig.EXT_AUTH_TYPE);
        
        this.externalLoginLink = this.config.externaLoginLink + this.config.serverConfig.EXT_AUTH_TYPE + '&locale=' + this.languageService.language;
    }

    @ViewChild('password')
    public passwordControl : ElementRef<HTMLInputElement>;

    private subscriptions = []

    public ngOnDestroy() {
        this.subscriptions.forEach((current) => {
            current.unsubscribe();
        })
    }

    public supportedLoginNames: any = {
        siemensid: __i18n('Siemens ID', 'siemensid', 'The caption displayed for the \'siemensid\' authentication provider.'),
        office365: __i18n('Microsoft', 'office365', 'The caption displayed for the \'office365\' authentication provider.'),
        climatixid: __i18n('Climatix ID', 'climatixid', 'The caption displayed for the \'climatixid\' authentication provider.')
    };

    public loginButtonState: string;

    public showSignup: boolean;

    public showInternalLogin: boolean = true;
    public showExternalLogin: boolean;
    public externalLoginText: string;
    public externalLoginLink: string;
    private externalAuthenticationName: string;
    public disableEmail: boolean = false;
    public disablePassword: boolean = false;

    public externalLoginDomains: {pattern: string, provider: string, name: string}[] = [];

    public disableInternalLogin: boolean = false;

    public loginWithSpecificExternalLoginText: string = 'Specific external login';
    private getExtLoginButtonCaption(provider) {
        return __i18n('Login with {name}', 'login.specificexternallogin.button.formattext')
            .replace('{name}', provider);
    }

    private getProviderAlias(provider) {
        if (provider.indexOf('climatixid-') === 0) {
            return provider.substring(11);
        }
        return provider;
    }

    private handleExternalLoginDomains(value) {
        for (let i = 0; i < this.externalLoginDomains.length; i++) {
            const current = this.externalLoginDomains[i];

            //if external login domain matches username, disable password
            if (value 
                && (
                    (current.pattern.startsWith('*') && value.endsWith(current.pattern.substring(1, current.pattern.length)))
                    || (!current.pattern.startsWith('*') && value.endsWith('@' + current.pattern))
                )
            ) {
                this.disableInternalLogin = true;
                const extProvider = this.getProviderAlias(current.provider || this.config.serverConfig.EXT_AUTH_TYPE);
                this.loginWithSpecificExternalLoginText = this.getExtLoginButtonCaption(
                    current.name
                    || (extProvider === this.config.serverConfig.EXT_AUTH_TYPE ? this.externalAuthenticationName : null)
                    || this.supportedLoginNames[extProvider]
                    || extProvider
                );
                this.externalLoginLink = this.config.externaLoginLink + extProvider + '&locale=' + this.languageService.language;
                break;
            }
        }
    }

    public usernameInput(e){
        this.externalLoginLink = this.config.externaLoginLink + this.config.serverConfig.EXT_AUTH_TYPE + '&locale=' + this.languageService.language;
        const value = e.target.value;
        if (this.externalLoginDomains && this.externalLoginDomains.length && !this.config.serverConfig.SIGNUP_EMAIL) {
            this.disableInternalLogin = false;
            this.handleExternalLoginDomains(value);
        }

        if (e.key === 'Enter') {
            if (!this.showInternalLogin || this.disableInternalLogin) {
                window.location.href = this.externalLoginLink +  this.getUserName();
            } else if (this.passwordControl) {
                this.passwordControl.nativeElement.focus();
            }
        }
    }

    public async passwordInput(e) {
        if (e.key === 'Enter') {
            console.log('passwort before enter input')
            e.preventDefault();
            e.stopPropagation();
            await this.login();
            
        }
    }

    getUserName() {
        return '&username=' + encodeURIComponent(this.loginForm.controls.UserName.value);
    }

    private isLoggingIn = false;
    public async login() {
        // check is needed because it is not possible to remove the submit event from the password field
        if(this.isLoggingIn) {
            return false;
        }
        this.isLoggingIn = true;
        this.authError = null;
        this.formHelper.markFormGroupTouched(this.loginForm);
        if (this.loginForm.valid) {
           
            try {
                this.loginButtonState = 'loading';
                const result = await this.authService.authenticate(
                    this.loginForm.controls.UserName.value,
                    this.loginForm.controls.Password.value,
                    this.loginForm.controls.RememberMe.value
                );
                if (!result) {
                    this.authError = 'The username or password provided is incorrect.';
                }
                this.loginButtonState = 'loaded';
            } catch (e) {
                let message: string = null;
                if(e?.status && e.status === 400 && e?.error?.PasswordExpired) {
                    this.router.navigateByUrl('/PasswordExpired')
                    return;
                }
                if (e && e.status === 400 && e.error && e.error.Message) {
                    try {
                        const msg = JSON.parse(e.error.Message);
                        if (typeof msg.errorCode === 'number') {
                            switch (msg.errorCode) {
                                case AuthResultCode.ExternalLoginAlreadyInUse:
                                    message = __i18n('The external login is already in use.',
                                        'login.externallogininuse.message');
                                    break;

                                case AuthResultCode.OnlyOneExternalLoginAllowed:
                                    message = __i18n(
                                        'This account already has an external login assigned. You cannot assign more than one external login.',
                                        'login.onlyoneexternallogin.message');
                                    break;
                                case AuthResultCode.OnlyExternalLoginAllowed:
                                    message = __i18n(
                                        'Only external login are allowed.',
                                        'login.onlyexternalloginallowed.message');
                                    break;
                            }
                        }
                    } catch (ex) {
                        console.warn('Failed to deserialize message: ' + e.error.Message);
                        this.isLoggingIn = false;
                    }
                }
                if (message === null) {
                    message = (e && e.error && e.error.Message)
                        ? e.error.Message
                        : __i18n('The username or password provided is incorrect.', 'loign.wrong.password.message');
                }
                this.isLoggingIn = false;
                this.authError = message;
                this.loginButtonState = 'loaded';
            }
        }
        this.isLoggingIn = false;
    }

    public authError: any = null;

    public loginForm: any;
}
